"use client"

import { motion, useScroll, useTransform } from "framer-motion"
import { ArrowRight } from "lucide-react"
import { useRef } from "react"
import { getFreeTrialHref } from "./pricing-anchors"

interface HeroSectionProps {
  content: Record<string, string>
}

export function HeroSection({ content }: HeroSectionProps): JSX.Element {
  const sectionRef = useRef<HTMLDivElement>(null)

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  })
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"])
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"])
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "6%"])



  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { delayChildren: 0.15, staggerChildren: 0.12 },
    },
  }

  const itemVariants = {
    hidden: { y: 28, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] as const },
    },
  }

  return (
    <section
      ref={sectionRef}
      className="relative flex flex-col justify-center overflow-hidden bg-[#0d2e1a] min-h-[calc(100vh-64px)]"
      aria-label="Présentation Dendromap"
    >
      {/* ─── Background atmosphere ──────────────────────────────────── */}
      <motion.div
        className="absolute inset-0 pointer-events-none select-none"
        style={{ y: bgY }}
        aria-hidden
      >
        {/* Radial glows */}
        <div className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full bg-emerald-600/10 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-emerald-800/15 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-emerald-500/5 blur-[80px]" />
        {/* Subtle dot grid */}
        <div
          className="absolute inset-0 opacity-[0.055]"
          style={{
            backgroundImage: "radial-gradient(circle, #4ade80 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        {/* Diagonal lines accent */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, #ffffff 0px, #ffffff 1px, transparent 1px, transparent 40px)",
          }}
        />
      </motion.div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-16 xl:gap-24 items-center">

          {/* ─── Left: Content ──────────────────────────────────────── */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ y: textY }}
            className="max-w-2xl"
          >
            {/* Badge */}
            {/* <motion.div variants={itemVariants} className="mb-7">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.12em] text-emerald-300 uppercase border border-emerald-500/30 rounded-full px-4 py-1.5 bg-emerald-500/10 backdrop-blur-sm">
                <TreePine className="size-3.5" aria-hidden />
                Référence arboricole française
              </span>
            </motion.div> */}

            {/* Headline */}
            <motion.h1
              variants={itemVariants}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white leading-[0.92] mb-6"
            >
              Transformez la gestion{" "}
              <span
                className="text-transparent bg-clip-text block"
                style={{
                  backgroundImage: "linear-gradient(135deg, #4ade80 0%, #00d084 45%, #22c55e 100%)",
                }}
              >
                de vos arbres
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              variants={itemVariants}
              className="text-lg text-white/60 leading-[1.7] mb-10 max-w-xl"
            >
              {content["hero.description"] ||
                "Cartographiez, inventoriez et gérez votre patrimoine arboré en un seul outil — conçu par des arboristes, pour des arboristes."}
            </motion.p>

            {/* CTAs */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-14"
            >
              <motion.a
                href={getFreeTrialHref()}
                whileHover={{
                  scale: 1.04,
                  boxShadow: "0 0 36px rgba(74,222,128,0.35)",
                }}
                whileTap={{ scale: 0.97 }}
                className="hover-shimmer inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl bg-emerald-500 text-white font-semibold text-[15px] hover:bg-emerald-400 transition-colors shadow-xl shadow-emerald-900/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400"
              >
                Démarrer un essai gratuit
                <ArrowRight className="size-4" aria-hidden />
              </motion.a>
              <motion.a
                href="#services"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-white/20 text-white/80 font-medium text-[15px] hover:bg-white/[0.07] hover:text-white hover:border-white/35 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
              >
                Voir la démonstration
              </motion.a>
            </motion.div>


          </motion.div>

          {/* ─── Right: App mockup ──────────────────────────────────── */}
          <motion.div
            className="relative hidden lg:flex items-center justify-center"
            initial={{ x: 60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: imageY }}
          >
            {/* Glow */}
            <div
              className="absolute inset-0 rounded-3xl blur-3xl scale-110 opacity-30 pointer-events-none"
              style={{ background: "radial-gradient(ellipse, #22c55e 0%, transparent 70%)" }}
              aria-hidden
            />

            {/* Dashboard frame */}
            <motion.div
              className="relative w-full max-w-[520px]"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5.5, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
            >
              {/* Browser chrome */}
              <div className="rounded-2xl overflow-hidden shadow-[0_32px_80px_-12px_rgba(0,0,0,0.7)] border border-white/[0.08] bg-[#071a11]">

                <img
                  src="/images/phone-dendro.png"
                  alt="Interface tableau de bord Dendromap — cartographie interactive"
                  className="w-full h-auto block"
                  loading="eager"
                />
              </div>

              {/* Floating notification — bottom left */}
              {/* <motion.div
                className="absolute -bottom-4 -left-10 bg-white dark:bg-gray-900 rounded-2xl p-3.5 shadow-2xl shadow-black/30 border border-gray-100 dark:border-gray-700 flex items-center gap-3 min-w-[210px]"
                initial={{ opacity: 0, scale: 0.85, x: -10, y: 10 }}
                animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                transition={{ delay: 1.3, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="size-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center shrink-0">
                  <TreePine className="size-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white text-xs leading-tight">
                    Arbre ajouté
                  </p>
                  <p className="text-gray-400 dark:text-gray-500 text-xs truncate">
                    Chêne pédonculé · Parc central
                  </p>
                </div>
                <span
                  className="size-2 rounded-full bg-emerald-500 animate-pulse shrink-0"
                  aria-hidden
                />
              </motion.div> */}

              {/* Floating stat badge — top right */}
              {/* <motion.div
                className="absolute -top-4 -right-8 bg-white dark:bg-gray-900 rounded-2xl px-4 py-3 shadow-xl shadow-black/20 border border-gray-100 dark:border-gray-700"
                initial={{ opacity: 0, scale: 0.85, x: 10, y: -10 }}
                animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                transition={{ delay: 1.5, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="text-xs text-gray-500 dark:text-gray-400">Arbres gérés</p>
                <p
                  className="text-2xl font-bold text-transparent bg-clip-text"
                  style={{
                    backgroundImage: "linear-gradient(135deg, #16a34a, #22c55e)",
                  }}
                >
                  1 284
                </p>
              </motion.div> */}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* ─── Wave divider ───────────────────────────────────────────── */}
      <div
        className="absolute bottom-0 left-0 right-0 overflow-hidden leading-[0] pointer-events-none"
        aria-hidden
      >
        <svg
          viewBox="0 0 1440 72"
          preserveAspectRatio="none"
          className="block w-full h-14 md:h-[72px]"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,36 C280,72 560,0 720,36 C880,72 1160,0 1440,36 L1440,72 L0,72 Z"
            className="fill-background"
          />
        </svg>
      </div>
    </section>
  )
}
